/**
 * The Tenant Theme Resolver
 * ------------------------
 * Phase 4.2 — future white-label support.
 *
 * Pure resolution logic only: given a tenant's binding, decide which
 * theme id should be used. Nothing here reads from or writes to the
 * outside world -- no storage, no network, no ThemeEngine instance --
 * so it can be called from anywhere (Core, a server-side resolver, a
 * test) and always produce the same output for the same input.
 *
 * The only import is a type-only reference to `ITenantThemeBinding`
 * (from the Phase 4.1 contract) so the exported function has a real,
 * checked parameter shape instead of an untyped object. A type-only
 * import is erased at compile time, so it carries no runtime
 * dependency and doesn't compromise the "no side effects, nothing to
 * fail" guarantee this resolver is meant to have.
 */
/**
 * Last-resort theme id, returned when nothing else in the priority
 * chain below produced a usable value. This mirrors the engine's own
 * `defaultThemeId: 'default'` configuration, so a caller who ignores
 * everything else about resolution still lands on a theme that's
 * known to exist.
 */
const HARD_DEFAULT_THEME_ID = 'default';
/**
 * The resolver's own built-in safety net, one tier above the hard
 * default. Distinct from `HARD_DEFAULT_THEME_ID` so a system-wide
 * fallback theme can be swapped in here later without touching the
 * absolute last-resort value above it.
 */
const SYSTEM_FALLBACK_THEME_ID = 'system-fallback';
/** Metadata keys this resolver looks at, until they earn first-class fields on ITenantThemeBinding. */
const TENANT_DEFAULT_THEME_METADATA_KEY = 'tenantDefaultThemeId';
const SYSTEM_FALLBACK_THEME_METADATA_KEY = 'systemFallbackThemeId';
function isNonEmptyString(value) {
    return typeof value === 'string' && value.trim().length > 0;
}
function readMetadataThemeId(metadata, key) {
    if (!metadata || typeof metadata !== 'object') {
        return undefined;
    }
    const value = metadata[key];
    return isNonEmptyString(value) ? value.trim() : undefined;
}
/**
 * Resolves the theme id a tenant should be rendered with.
 *
 * Priority, first match wins:
 *   1. Tenant custom theme  -- `binding.themeId`, the explicit per-tenant override.
 *   2. Tenant default theme -- `binding.metadata.tenantDefaultThemeId`, an
 *      account-level fallback distinct from a one-off override. Carried in
 *      `metadata` (the contract's documented escape hatch) until it earns
 *      a first-class field on `ITenantThemeBinding`.
 *   3. System fallback theme -- `binding.metadata.systemFallbackThemeId` if
 *      provided, else this resolver's own `SYSTEM_FALLBACK_THEME_ID` constant.
 *   4. `"default"` -- absolute last resort.
 *
 * Never throws: malformed, partial, or entirely missing input (including
 * `null`/`undefined`, or fields of the wrong runtime type despite the
 * static type) is treated as "no value at this tier" and falls through
 * to the next one, ending at `"default"`.
 */
export function resolveTenantTheme(binding) {
    try {
        if (binding && typeof binding === 'object') {
            // 1. Tenant custom theme
            if (isNonEmptyString(binding.themeId)) {
                return binding.themeId.trim();
            }
            const metadata = binding.metadata && typeof binding.metadata === 'object'
                ? binding.metadata
                : undefined;
            // 2. Tenant default theme
            const tenantDefaultThemeId = readMetadataThemeId(metadata, TENANT_DEFAULT_THEME_METADATA_KEY);
            if (tenantDefaultThemeId) {
                return tenantDefaultThemeId;
            }
            // 3. System fallback theme (metadata override, else built-in constant)
            const systemFallbackThemeId = readMetadataThemeId(metadata, SYSTEM_FALLBACK_THEME_METADATA_KEY);
            if (systemFallbackThemeId) {
                return systemFallbackThemeId;
            }
        }
        if (isNonEmptyString(SYSTEM_FALLBACK_THEME_ID)) {
            return SYSTEM_FALLBACK_THEME_ID;
        }
    }
    catch {
        // Fall through -- this resolver must never throw regardless of
        // what shape (or non-shape) `binding` turns out to be at runtime.
    }
    // 4. Absolute last resort.
    return HARD_DEFAULT_THEME_ID;
}
